import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  private defaultLang: string = 'en';
  private translateService: TranslateService = inject(TranslateService);
  private readonly STORAGE_KEY = 'selectedLanguage';

  // Mapping des codes de langue alternatifs vers les fichiers de traduction disponibles
  private readonly LANGUAGE_MAPPING: Record<string, string> = {
    us: 'en-EN',
    'en-us': 'en-EN',
    'en-gb': 'en-EN',
    en: 'en-EN',
    fr: 'fr-FR',
    de: 'de-DE',
    es: 'es-ES',
    it: 'it-IT',
    nl: 'nl-NL',
    jp: 'jp-JP',
    ja: 'jp-JP',
  };

  /**
   * Résout un code de langue vers un fichier de traduction disponible
   * @param langCode code de langue (ex: 'us', 'en-us', 'fr')
   * @returns le code de langue formaté correspondant (ex: 'en-EN', 'fr-FR')
   */
  private resolveLangCode(langCode: string): string {
    // Normaliser le code en minuscules
    const normalizedCode: string = langCode.toLowerCase();

    // Vérifier d'abord le mapping direct
    if (this.LANGUAGE_MAPPING[normalizedCode]) {
      return this.LANGUAGE_MAPPING[normalizedCode];
    }

    // Si c'est déjà au format xx-XX, vérifier s'il existe
    if (normalizedCode.includes('-')) {
      const formattedCode: string = this.formatLang(
        normalizedCode.split('-')[0]
      );
      if (this.translateService.getLangs().includes(formattedCode)) {
        return formattedCode;
      }
    }

    // Fallback vers le formatage standard
    return this.formatLang(normalizedCode);
  }

  /**
   * Déduit la langue du préfixe d'URL du redirect_uri
   * @param redirectUri URL de retour du client
   * @returns le préfixe de langue s'il est connu, sinon null
   */
  getLangFromRedirectUri(redirectUri: string | undefined): string | null {
    if (!redirectUri) return null;

    try {
      const prefix: string | undefined = new URL(redirectUri).pathname
        .split('/')[1]
        ?.toLowerCase();
      return prefix && this.LANGUAGE_MAPPING[prefix] ? prefix : null;
    } catch {
      return null;
    }
  }

  setLanguage(langParam: string | null): void {
    // 1 - si il y a un param de lang
    if (langParam) {
      const resolvedLang: string = this.resolveLangCode(langParam);
      if (this.isLangAvailable(resolvedLang)) {
        this.translateService.use(resolvedLang);
        localStorage.setItem(this.STORAGE_KEY, resolvedLang);
        return;
      }
    }

    // 2 - sinon on vérifie si une langue est déjà stockée
    const storedLang: string | null = localStorage.getItem(this.STORAGE_KEY);
    if (storedLang && this.isLangAvailable(storedLang)) {
      this.translateService.use(storedLang);
      return;
    }

    // 3 - sinon on test le navigateur (seulement si pas de langue stockée)
    const navigatorLang: string = navigator.language;
    if (navigatorLang && this.isLangAvailable(navigatorLang)) {
      const formattedLang: string = this.formatLang(navigatorLang);
      this.translateService.use(formattedLang);
      localStorage.setItem(this.STORAGE_KEY, formattedLang);
      return;
    }

    // 4 - sinon default (seulement si pas de langue stockée)
    const defaultFormattedLang: string = this.formatLang(this.defaultLang);
    this.translateService.use(defaultFormattedLang);
    localStorage.setItem(this.STORAGE_KEY, defaultFormattedLang);
  }

  /**
   * Récupère la langue actuellement utilisée
   * @returns la langue actuelle (ex: 'fr-FR')
   */
  getCurrentLanguage(): string {
    return (
      this.translateService.currentLang || this.formatLang(this.defaultLang)
    );
  }

  /**
   * Change la langue et la persiste
   * @param langCode code de langue (ex: 'fr', 'en', 'it', 'us')
   */
  changeLanguage(langCode: string): void {
    const resolvedLang: string = this.resolveLangCode(langCode);
    if (this.isLangAvailable(resolvedLang)) {
      this.translateService.use(resolvedLang);
      localStorage.setItem(this.STORAGE_KEY, resolvedLang);
    }
  }

  /**
   * Transforme un lang code en langue formattée
   * @param langCode e.g. 'en', 'fr', 'de'
   * @returns fr-FR
   */
  formatLang(langCode: string): string {
    return `${langCode.slice(0, 2).toLowerCase()}-${langCode
      .slice(0, 2)
      .toUpperCase()}`;
  }

  /**
   * Verifie si la langue est prise en compte dans l'app
   * @param langCode code de langue (ex: 'fr' ou 'fr-FR')
   * @returns
   */
  isLangAvailable(langCode: string): boolean {
    const availableLangs: readonly string[] = this.translateService.getLangs();
    // Si le code est déjà formaté (ex: 'fr-FR'), on le teste directement
    if (langCode.includes('-')) {
      return availableLangs.includes(langCode);
    }
    // Sinon on le formate d'abord
    return availableLangs.includes(this.formatLang(langCode));
  }
}
